import { OddResponse } from '../odds/types/odds';
import {
  IConfigDate,
  IConfigEntityLinks,
  IDictionary,
  ILabels,
  INormalizedCompetition,
  Markets,
  OddMarketValueTypes,
  RefreshTimes,
  ShortNameType,
  ShortStatusType,
  Themes,
  WidgetType,
} from './common';

export interface INormalizedEvents {
  competition: INormalizedCompetition;
  events: INormalizedEvent[];
}

export interface INormalizedStageEvents {
  competition: INormalizedCompetition;
  events: {
    stage: {
      id: string;
      name: string;
      target: string;
      url: string;
    };
    data: INormalizedRoundEvents[];
  }[];
}

export interface INormalizedRoundEvents {
  round: {
    id: string;
    name: string;
  };
  events: INormalizedEvent[];
}

export interface INormalizedEvent {
  id: string;
  date: string;
  startTime: string;
  status: string;
  statusType: string;
  isLive: boolean;
  elapsed: string;
  eventUrl?: string;
  target: string;
  participants: IParticipant[];
  odds: OddResponse[];
  labels: string[];
}

export interface IParticipant {
  id: string;
  logo: string;
  name: string;
  isWinner: boolean;
  results: IParticipantResults[];
  isNational: boolean;
}

export interface IParticipantResults {
  label: string;
  value: string;
  isWinner: boolean;
  isFinal: boolean;
}

export enum ResultTypes {
  QUARTER1 = 'quarter1',
  QUARTER2 = 'quarter2',
  QUARTER3 = 'quarter3',
  QUARTER4 = 'quarter4',
  PERIOD1 = 'period1',
  PERIOD2 = 'period2',
  PERIOD3 = 'period3',
  PERIOD4 = 'period4',
  EXTRA_TIME = 'extratime',
  FINAL_RESULT = 'display',
  PENALTY_SHOOTOUT = 'penaltyshootout',
}

export interface INormalizedGame {
  competition: ICompetition;
  firstTeam: ITeam;
  secondTeam: ITeam;
  id: string;
  status: string;
  isLive: boolean;
  startTime: string;
  isStarted: boolean;
  odds: OddResponse[];
  url: string;
  target: string;
}

interface ICompetition {
  id: string;
  name: string;
  date: string;
  url: string;
}

export interface ITeam {
  id: string;
  name: string;
  logo: string;
  url: string;
  isWinner: boolean;
  score: string;
  penaltyScore: string;
}

export interface IMultiSportLivescoreConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataDate?: IConfigDate;
  dataCompetitions?: string[] | null;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsMarket?: Markets;
  dataOddsPreEventOnly?: boolean;
  dataOddsMarketValueType?: OddMarketValueTypes;
  dataTeamNameType?: ShortNameType | null;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataPopularList?: string;
  dataLabels?: IMultiSportLivescoreDataLabels | null;
  dataMatchId?: string;
  dataHeaderDefaultOption?: string;
  dataHeaderDisplay?: boolean;
  dataAllowInlineOdds?: boolean;
  dataOptionsDisplay?: boolean;
  dataBettingBrandColors?: boolean;
  dataBettingLogoClickAction?: boolean;
  dataBettingLogoOverlap?: boolean;
}

export interface IMultiSportSingleEventConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsMarket?: Markets;
  dataOddsPreEventOnly?: boolean;
  dataOddsMarketValueType?: OddMarketValueTypes;
  dataTeamNameType?: ShortNameType | null;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataLabels?: ILabels | null;
  dataTheme?: Themes | null;
  dataPopularList?: string;
  dataMatchId?: string;
  dataWrapperLink?: boolean;
  dataAdditionalInfo?: boolean;
  dataShortStatusType?: ShortStatusType | null;
  dataBettingBrandColors?: boolean;
  dataBettingLogoClickAction?: boolean;
  dataBettingLogoOverlap?: boolean;
}

export interface IMultiSportLivescoreDataLabels extends IDictionary<string | undefined> {
  all?: string;
  live?: string;
  no_games?: string;
  odds?: string;
  overtime?: string;
  points?: string;
  popular?: string;
  score?: string;
  standings?: string;
  upcoming?: string;
}

export type IMultiSportConfig = IMultiSportLivescoreConfig & IMultiSportSingleEventConfig;
